import { useMemo } from "react";

import cn from "classnames";

import { useTextCoreStore } from "../store/store";

import type { TextCoreChildProps } from "../types";

import classes from "../TextCore.styl";

export function useTextChildProps(
    props: TextCoreChildProps,
    additionalClasses?: string,
) {
    const parentCtx = useTextCoreStore();

    const {
        typographyStyle,
        textCase,
        underline,
        strikethrough,
        alignmentX,
        lineHeight,
        italic,
        quote,
        ...filteredProps
    } = props;

    const resolvedProps = useMemo(
        () => ({
            typographyStyle:
                typographyStyle || parentCtx.typographyStyle || "body-l",
            textCase: textCase || parentCtx.textCase || "asTyped",
            alignmentX: alignmentX || parentCtx.alignmentX || "start",
            underline:
                !parentCtx.underline || parentCtx.underline === "none"
                    ? underline
                    : "none",
            strikethrough: parentCtx.strikethrough ? undefined : strikethrough,
            lineHeight: lineHeight || parentCtx.lineHeight || "inherit",
            italic: italic !== undefined ? italic : parentCtx.italic,
            cited: parentCtx.quote ? false : quote,
        }),
        [
            typographyStyle,
            textCase,
            underline,
            strikethrough,
            alignmentX,
            lineHeight,
            italic,
            quote,
            parentCtx.typographyStyle,
            parentCtx.textCase,
            parentCtx.alignmentX,
            parentCtx.underline,
            parentCtx.strikethrough,
            parentCtx.lineHeight,
            parentCtx.italic,
            parentCtx.quote,
        ],
    );

    const className = useMemo(
        () =>
            cn(
                classes["text-core"],
                resolvedProps.typographyStyle !== "custom" &&
                    classes[`__${resolvedProps.typographyStyle}`],
                resolvedProps.alignmentX !== "start" &&
                    classes[`__alignment-x-${resolvedProps.alignmentX}`],
                resolvedProps.textCase !== "asTyped" &&
                    classes[`__case-${resolvedProps.textCase}`],
                resolvedProps.underline &&
                    resolvedProps.underline !== "none" &&
                    classes[`__underline-${resolvedProps.underline}`],
                resolvedProps.strikethrough && classes["__strikethrough"],
                resolvedProps.italic && classes["__italic"],
                resolvedProps.cited && classes["__cited"],
                additionalClasses,
            ),
        [resolvedProps, additionalClasses],
    );

    const style = useMemo(
        () => ({ "--line-height": resolvedProps.lineHeight }),
        [resolvedProps.lineHeight],
    );

    return {
        className,
        style,
        filteredProps,
    };
}
